<template>
  <div>
    <label
      class="input_label font_regular font_500 text_12 mb_16"
      :class="{ error: error, [classNameLabel]: true }"
    >
      {{ label }}
      <span v-if="infoList.length" class="tooltip_info">
        <fluent-icon class="help_icon" :icon="infoIcon" />
        <div class="tooltip_card">
          <p
            v-for="(line, index) in infoList"
            :key="index"
            class="mb_0 font_12"
          >
            {{ line }}
          </p>
        </div>
      </span>
      <textarea
        v-if="type === 'textarea'"
        ref="inputField"
        :value="value"
        :type="type"
        :disabled="disabled"
        class="text_14 font_500 pt_10 pb_10 pl_12 pr_12 mt_8"
        :class="[className].join(' ')"
        :placeholder="placeholder"
        :rows="rows"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
      />
      <input
        v-else
        ref="inputField"
        v-bind="$attrs"
        :value="value"
        :type="type"
        :disabled="disabled"
        class="text_14 font_500 pt_10 pb_10 pl_12 pr_12 mt_8 round_corner"
        :class="[className].join(' ')"
        :placeholder="placeholder"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @keydown.enter="onEnter"
      />
    </label>
    <p v-if="error" class="error-message text_12 font_500 !mt-4">
      {{ error }}
    </p>
    <span
      v-else-if="hint"
      class="hint font_regular text_12 font_500 text_black_secondary"
    >
      {{ hint }}
    </span>
    <span
      v-if="postFix"
      class="postfix text_14"
      :class="classNamePostFix"
      :style="{ left: postFixLeft + 'px' }"
    >
      {{ postFix }}
    </span>
  </div>
</template>

<script>
export default {
  props: {
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      // required: true,
      default: null,
    },
    error: {
      type: String,
      default: '',
    },
    className: {
      type: String,
      default: '',
    },
    classNameLabel: {
      type: String,
      default: '',
    },
    classNamePostFix: {
      type: String,
      default: '',
    },
    infoIcon: {
      type: String,
      default: 'question-circle',
    },
    infoList: {
      type: Array,
      default: () => [],
    },
    hint: {
      type: String,
      default: '',
    },
    textLength: {
      type: [String, Number],
      default: '',
    },
    postFix: {
      type: [String, Number],
      default: '',
    },
    rows: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      inputWidth: 100,
    };
  },
  computed: {
    postFixLeft() {
      if (!this.textLength || !this.postFix) {
        return 80;
      }
      const base = 20;
      let inputWidth = this.$refs.inputField
        ? this.$refs.inputField.offsetWidth
        : this.inputWidth;
      if (inputWidth > 0) {
        inputWidth -= 35;
      }
      let textWidth =
        `${this.textLength}`.length > 0
          ? this.getTextWidth(`${this.textLength}`)
          : 50;
      if (textWidth < 0) {
        this.$nextTick(() => {
          textWidth = this.getTextWidth(`${this.textLength}`);
          return base + textWidth;
        });
      }
      if (textWidth > inputWidth) {
        textWidth = inputWidth;
      }
      return base + textWidth;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateInputWidth(); // Ensure correct width after first render
    });
    window.addEventListener('resize', this.updateInputWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateInputWidth);
  },
  methods: {
    onChange(event) {
      this.$emit('input', event.target.value);
    },
    onInput(event) {
      this.$emit('input', event.target.value);
    },
    onBlur(event) {
      this.$emit('blur', event.target.value);
    },
    onEnter(event) {
      this.$emit('enter', event.target.value);
    },
    getTextWidth(text) {
      // Create an offscreen canvas element
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // Set the font for the context
      context.font = '14px Figtree';

      // Measure the text width
      const textWidth = context.measureText(text).width;

      return textWidth;
    },
    updateInputWidth() {
      if (!this.textLength || !this.postFix) {
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.inputField) {
          this.inputWidth = this.$refs.inputField.offsetWidth || 100;
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/widgets/_fonts.scss';

.input_label {
  position: relative;
  input {
    min-height: 4.2rem;
    border-color: #eeeeee;
    &:disabled {
      background-color: #eeeeee;
    }
  }
}

.help_tooltip {
  position: relative;
}

.help_icon {
  position: absolute;
  top: 0;
  left: 8px;
  width: 15px;
  height: 15px;
}

.tooltip_info {
  position: relative;

  &:hover {
    .tooltip_card {
      display: block;
    }
  }

  .tooltip_card {
    display: none;
    position: absolute;
    bottom: 12px;
    left: -17px;
    width: 100%;
    z-index: 1;
    min-width: 300px;
    padding: 5px 5px 5px 22px;
    background: #000;
    color: #fff;
    border-radius: 4px;
  }
}

.hint {
  display: block;
  margin-top: -1.6rem;
}

.postfix {
  position: absolute;
  top: 37px;
}

.round_corner {
  border-radius: var(--border-radius-normal);
}

.error-message {
  color: var(--r-500);
  font-family: 'Figtree', sans-serif;
}
</style>
